import React from "react";
import OrderedModuleEditor from "@/components/common/OrderedModuleEditor";
import MediaImageField from "@/components/common/MediaImageField";
import { ImgWithFallback, serviceStaticUrl } from "@/lib/serviceMedia";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const Field = ({ label, children, hint }) => (
  <div className="space-y-1">
    <Label className="text-xs text-white/50">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-white/40">{hint}</p>}
  </div>
);

export default function Services() {
  return (
    <OrderedModuleEditor
      resource="services"
      singular="service"
      title="Services"
      subtitle="Drag to reorder service cards. Upload images or fall back to website static assets."
      titleKey="title"
      defaultValues={{
        order: 1,
        title: "",
        slug: "",
        description: "",
        media: null,
        icon: "",
        category: "",
        status: "published",
      }}
      getItemLabel={(it, idx) => it.title || `Service ${idx + 1}`}
      renderPreviewItem={(it) => {
        const fallback = serviceStaticUrl(it.slug);
        return (
          <div className="flex gap-3 items-start">
            {(it.media?.src || fallback) ? (
              <ImgWithFallback
                src={it.media?.src}
                fallback={fallback}
                alt=""
                className="w-16 h-16 rounded-lg object-cover shrink-0 bg-white/5"
              />
            ) : (
              <span className="w-16 h-16 rounded-lg glass flex items-center justify-center text-xs font-mono text-white/60 shrink-0">
                {it.icon || "SV"}
              </span>
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-white">{it.title || "Untitled"}</div>
              <p className="text-xs text-white/50 mt-1 line-clamp-2">{it.description || ""}</p>
              <div className="flex items-center gap-2 mt-1.5">
                {it.category ? <span className="text-[11px] text-white/40">{it.category}</span> : null}
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    it.status === "published" ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-white/40"
                  }`}
                >
                  {it.status || "draft"}
                </span>
              </div>
            </div>
          </div>
        );
      }}
      renderInspector={(item, patch) => (
        <>
          <Field label="Title">
            <Input
              value={item.title || ""}
              onChange={(e) => patch({ title: e.target.value })}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <Field label="Slug" hint="URL-friendly identifier used by service pages">
            <Input
              value={item.slug || ""}
              onChange={(e) => patch({ slug: e.target.value })}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <Field label="Description">
            <Textarea
              value={item.description || ""}
              onChange={(e) => patch({ description: e.target.value })}
              rows={4}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <MediaImageField
            label="Service Image"
            value={item.media}
            onChange={(media) => patch({ media })}
            folder="services"
            valueMode="media"
            aspectClass="h-28"
            fallbackSrc={serviceStaticUrl(item.slug)}
            hint="Upload a new image, or keep the website static asset as fallback."
          />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Icon">
              <Input
                value={item.icon || ""}
                onChange={(e) => patch({ icon: e.target.value })}
                placeholder="BR, SM…"
                className="bg-black/20 border-white/10 text-white"
              />
            </Field>
            <Field label="Category">
              <Input
                value={item.category || ""}
                onChange={(e) => patch({ category: e.target.value })}
                placeholder="Creative, Development…"
                className="bg-black/20 border-white/10 text-white"
              />
            </Field>
          </div>
          <Field label="Status">
            <select
              value={item.status || "draft"}
              onChange={(e) => patch({ status: e.target.value })}
              className="w-full bg-black/20 border border-white/10 text-white rounded-md px-3 py-2 text-sm"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </Field>
        </>
      )}
    />
  );
}
